/**
 * MarketDataBus — Non-reactive data layer for heavy market data.
 *
 * WHY: Zustand stores trigger synchronous React re-renders across ALL subscribers
 * whenever any slice changes. For high-frequency, high-volume data (327 snapshots,
 * 115K rows), this creates cascading re-render storms that freeze the UI.
 *
 * HOW: This class stores data in plain JavaScript objects (zero React overhead).
 * Components subscribe via React 18's `useSyncExternalStore` with fine-grained
 * selectors, so they ONLY re-render when their specific data actually changes.
 *
 * ARCHITECTURE:
 *   All writes go through a requestAnimationFrame batch queue.
 *   This means multiple rapid setter calls (e.g., CSV worker completing 3 symbols
 *   in quick succession) are coalesced into a SINGLE _notify() call,
 *   causing exactly ONE React reconciliation cycle instead of 3.
 *
 * USAGE:
 *   // Writing (from fetchers/engines — no re-renders triggered):
 *   marketDataBus.setIntradayHistory('NIFTY', processedSnapshots)
 *
 *   // Reading (from components — granular re-renders via useSyncExternalStore):
 *   const history = useDataBus(bus => bus.getIntradayHistory('NIFTY'))
 */

import type { CogRow, MarketWatchHistoryState, OptionData } from '../types'

type Listener = () => void

/** Frozen empty array — returned for missing data to maintain referential stability.
 * FREEZE FIX: Using a single frozen reference prevents useSyncExternalStore from
 * seeing a "new" empty array on every call, which would trigger re-renders. */
const EMPTY: readonly any[] = Object.freeze([])

class MarketDataBus {
    // ─── Heavy Data Stores (Non-Reactive) ────────────────────────
    private _intradayHistory: Record<string, any[]> = {}
    private _historicalHistory: Record<string, OptionData[][]> = {}
    private _rangeHistory: Record<string, OptionData[]> = {}
    private _upstoxHistoryData: Record<string, any[]> = {}
    private _cogHistory: Record<string, CogRow[]> = {}
    private _marketWatchHistory: Record<string, MarketWatchHistoryState> = {}

    // ─── Version Tracking ────────────────────────────────────────
    private _versions = {
        intradayHistory: 0,
        historicalHistory: 0,
        rangeHistory: 0,
        upstoxHistoryData: 0,
        cogHistory: 0,
        marketWatchHistory: 0,
    }

    // ─── RAF + Coalesce Batch Queue ────────────────────────────────
    // FREEZE FIX v2: Two-stage batching to prevent cascade storms:
    //   Stage 1 (RAF):       Coalesce writes within the same animation frame (~16ms)
    //   Stage 2 (Coalesce):  After RAF fires, wait 50ms for follow-up writes
    //                        from other engines (CogEngine, MarketWatchEngine)
    //                        before notifying subscribers.
    //
    // On initial load, this reduces:
    //   3 notification cycles × 15 subscribers = 45 re-render waves
    //   → 1 notification cycle × 15 subscribers = 15 re-renders (3× reduction)
    //
    // Data is stored IMMEDIATELY on set() — reads are always instant.
    // Only the React re-render notification is deferred by ≤50ms.
    private _rafScheduled = false
    private _dirtyCategories = new Set<keyof typeof this._versions>()
    private _coalesceTimer: ReturnType<typeof setTimeout> | null = null

    private _scheduleNotify(category: keyof typeof this._versions) {
        this._dirtyCategories.add(category)

        // If a coalesce timer is already ticking (we're in the 50ms window),
        // reset it so this new write is included in the same notification batch.
        if (this._coalesceTimer) {
            clearTimeout(this._coalesceTimer)
            this._coalesceTimer = null
            // Flush immediately via the coalesce path (skip RAF, we're already past it)
            this._flushWithCoalesce()
            return
        }

        if (!this._rafScheduled) {
            this._rafScheduled = true

            // Use requestAnimationFrame to defer notification to next paint frame.
            if (typeof requestAnimationFrame !== 'undefined') {
                requestAnimationFrame(() => {
                    this._flushWithCoalesce()
                })
            } else {
                // SSR / Node.js fallback — use microtask
                Promise.resolve().then(() => {
                    this._flushWithCoalesce()
                })
            }
        }
    }

    private _flushWithCoalesce() {
        // Increment versions immediately so synchronous reads see latest data
        for (const cat of this._dirtyCategories) {
            this._versions[cat]++
        }
        this._dirtyCategories.clear()
        this._rafScheduled = false

        // Start/restart the 50ms coalesce window.
        // If another engine writes within 50ms, _scheduleNotify will
        // clear this timer and call _flushWithCoalesce again,
        // further deferring the notification.
        if (this._coalesceTimer) clearTimeout(this._coalesceTimer)
        this._coalesceTimer = setTimeout(() => {
            this._coalesceTimer = null
            this._notify()
        }, 50)
    }

    // ─── Subscriber Management ───────────────────────────────────
    private _listeners = new Set<Listener>()

    subscribe = (listener: Listener): (() => void) => {
        this._listeners.add(listener)
        return () => {
            this._listeners.delete(listener)
        }
    }

    private _notify() {
        for (const listener of this._listeners) {
            listener()
        }
    }

    // ─── Getters (Pure reads — no side effects) ──────────────────

    getIntradayHistory(symbol: string): any[] {
        return this._intradayHistory[symbol] || EMPTY as any[]
    }

    getHistoricalHistory(symbol: string): OptionData[][] {
        return (this._historicalHistory[symbol] || EMPTY) as OptionData[][]
    }

    getRangeHistory(symbol: string): OptionData[] {
        return (this._rangeHistory[symbol] || EMPTY) as OptionData[]
    }

    getUpstoxHistoryData(symbol: string): any[] {
        return this._upstoxHistoryData[symbol] || EMPTY as any[]
    }

    getCogHistory(symbol: string): CogRow[] {
        return (this._cogHistory[symbol] || EMPTY) as CogRow[]
    }

    getMarketWatchHistory(symbol: string): MarketWatchHistoryState | undefined {
        return this._marketWatchHistory[symbol]
    }

    /** Composite version for general "did anything change?" checks */
    getVersion(): number {
        return (
            this._versions.intradayHistory +
            this._versions.historicalHistory +
            this._versions.rangeHistory +
            this._versions.upstoxHistoryData +
            this._versions.cogHistory +
            this._versions.marketWatchHistory
        )
    }

    /** Fine-grained version for specific category */
    getCategoryVersion(category: keyof typeof this._versions): number {
        return this._versions[category]
    }

    // ─── Setters (RAF-batched writes) ────────────────────────────
    // FREEZE FIX: Every setter has NO-OP guards:
    //   1. Same reference → skip (identity check)
    //   2. Both previous and next are empty → skip (prevents empty→empty storms)
    //   3. Writes are stored immediately (for instant reads) but notification
    //      is deferred to requestAnimationFrame (for batched renders)

    setIntradayHistory(symbol: string, history: any[]) {
        const prev = this._intradayHistory[symbol]
        if (prev === history) return
        if ((!prev || prev.length === 0) && (!history || history.length === 0)) return
        this._intradayHistory[symbol] = history
        this._scheduleNotify('intradayHistory')
    }

    setHistoricalHistory(symbol: string, history: OptionData[][]) {
        const prev = this._historicalHistory[symbol]
        if (prev === history) return
        if ((!prev || prev.length === 0) && (!history || history.length === 0)) return
        this._historicalHistory[symbol] = history
        this._scheduleNotify('historicalHistory')
    }

    setRangeHistory(symbol: string, history: OptionData[]) {
        const prev = this._rangeHistory[symbol]
        if (prev === history) return
        if ((!prev || prev.length === 0) && (!history || history.length === 0)) return
        this._rangeHistory[symbol] = history
        this._scheduleNotify('rangeHistory')
    }

    setUpstoxHistoryData(symbol: string, data: any[]) {
        const prev = this._upstoxHistoryData[symbol]
        if (prev === data) return
        if ((!prev || prev.length === 0) && (!data || data.length === 0)) return
        this._upstoxHistoryData[symbol] = data
        this._scheduleNotify('upstoxHistoryData')
    }

    setCogHistory(symbol: string, history: CogRow[]) {
        const prev = this._cogHistory[symbol]
        if (prev === history) return
        if ((!prev || prev.length === 0) && (!history || history.length === 0)) return
        this._cogHistory[symbol] = history
        this._scheduleNotify('cogHistory')
    }

    setMarketWatchHistory(symbol: string, data: MarketWatchHistoryState) {
        const prev = this._marketWatchHistory[symbol]
        if (prev === data) return
        this._marketWatchHistory[symbol] = data
        this._scheduleNotify('marketWatchHistory')
    }

    /** Reset all data (e.g., on symbol change to prevent stale data bleed) */
    clearAll() {
        this._intradayHistory = {}
        this._historicalHistory = {}
        this._rangeHistory = {}
        this._upstoxHistoryData = {}
        this._cogHistory = {}
        this._marketWatchHistory = {}
        // clearAll is intentionally synchronous — it's called on symbol change
        // and we want all subscribers to see the empty state immediately
        for (const key of Object.keys(this._versions) as (keyof typeof this._versions)[]) {
            this._versions[key]++
        }
        this._notify()
    }
}

/** Singleton instance — import this everywhere */
export const marketDataBus = new MarketDataBus()
